/**
 * Progressive enhancement, and nothing else. Every form here already works as
 * a plain POST and redirect; this removes the page reloads, keeps the back
 * button honest, and switches on the few controls that need scripting. It
 * never creates an element - everything it touches came from the server.
 */

import "../styles/app.css";
import { ColorField } from "./color-field";

const THEME_KEY = "theme";
const THEMES = ["system", "light", "dark"];

document.addEventListener("submit", (event) => {
	const form = (event.target as HTMLElement | null)?.closest("form");
	if (!form || event.defaultPrevented) return;

	const submitter = (event as SubmitEvent).submitter as HTMLButtonElement | null;

	if (form.dataset.enhance === "theme") {
		event.preventDefault();
		applyTheme(submitter?.value ?? "system");
		return;
	}

	if (form.dataset.enhance === "swap") {
		event.preventDefault();
		void send(form, submitter);
	}
});

document.addEventListener("click", (event) => {
	const target = event.target as HTMLElement | null;

	const copyButton = target?.closest<HTMLButtonElement>("[data-copy]");
	if (copyButton) {
		event.preventDefault();
		void copy(copyButton);
		return;
	}

	const link = target?.closest("a");
	if (link && isInternal(link, event)) {
		event.preventDefault();
		void go(link.href, true);
		return;
	}

	const shareButton = target?.closest<HTMLButtonElement>("[data-share]");
	if (shareButton) {
		event.preventDefault();
		void navigator
			.share({
				title: "My number colors",
				text: "These are the colors my brain gives the digits 0-9.",
				url: shareUrl() ?? location.href,
			})
			.catch(() => undefined);
	}
});

window.addEventListener("popstate", () => {
	void go(location.href, false);
});

/** Same-site, plain left click, not a download and not a new tab. */
function isInternal(link: HTMLAnchorElement, event: MouseEvent): boolean {
	return (
		link.origin === location.origin &&
		!link.hasAttribute("download") &&
		!link.target &&
		event.button === 0 &&
		!event.metaKey &&
		!event.ctrlKey &&
		!event.shiftKey &&
		!event.altKey &&
		!event.defaultPrevented
	);
}

restoreTheme();
syncOptionalControls();

/* Theme ------------------------------------------------------------------- */

/** localStorage owns the choice; the cookie only lets the server render it. */
function applyTheme(theme: string): void {
	const value = THEMES.includes(theme) ? theme : "system";
	const root = document.documentElement;
	if (value === "system") root.removeAttribute("data-theme");
	else root.dataset.theme = value;

	for (const button of document.querySelectorAll<HTMLButtonElement>('form[data-enhance="theme"] button[name="theme"]')) {
		button.setAttribute("aria-pressed", String(button.value === value));
	}

	document.cookie = `theme=${value}; path=/; max-age=31536000; samesite=lax${
		location.protocol === "https:" ? "; secure" : ""
	}`;
	try {
		localStorage.setItem(THEME_KEY, value);
	} catch {
		// Private mode, or storage turned off: the cookie still carries it.
	}

	tuneEmptyPicker();
}

function restoreTheme(): void {
	let stored: string | null = null;
	try {
		stored = localStorage.getItem(THEME_KEY);
	} catch {
		return;
	}
	if (!stored || !THEMES.includes(stored)) return;
	if (stored !== (document.documentElement.dataset.theme ?? "system")) applyTheme(stored);
}

/* Navigation -------------------------------------------------------------- */

async function send(form: HTMLFormElement, submitter: HTMLButtonElement | null): Promise<void> {
	const data = new FormData(form, submitter);
	const body = new URLSearchParams();
	for (const [key, value] of data) if (typeof value === "string") body.append(key, value);

	form.setAttribute("aria-busy", "true");
	try {
		const response = await fetch(form.action, {
			method: "POST",
			body,
			headers: { Accept: "text/html" },
			credentials: "same-origin",
		});
		await render(response, true);
	} catch {
		form.submit(); // The network said no - let the browser do it the old way.
	} finally {
		form.removeAttribute("aria-busy");
	}
}

async function go(url: string, push: boolean): Promise<void> {
	try {
		const response = await fetch(url, { headers: { Accept: "text/html" }, credentials: "same-origin" });
		await render(response, push);
	} catch {
		location.href = url;
	}
}

async function render(response: Response, push: boolean): Promise<void> {
	const html = await response.text();
	const incoming = new DOMParser().parseFromString(html, "text/html");
	const next = incoming.querySelector("main");
	const current = document.querySelector("main");
	if (!next || !current) {
		location.href = response.url;
		return;
	}

	const swap = (): void => {
		current.replaceWith(next);
		document.title = incoming.title;
		syncNav(incoming);
		syncOptionalControls();
	};

	if (push && response.url !== location.href) history.pushState(null, "", response.url);

	const startViewTransition = document.startViewTransition?.bind(document);
	if (startViewTransition && !matchMedia("(prefers-reduced-motion: reduce)").matches) {
		await startViewTransition(swap).finished.catch(() => undefined);
	} else {
		swap();
	}
}

function syncNav(incoming: Document): void {
	const links = document.querySelectorAll<HTMLAnchorElement>(".site-header nav a");
	const fresh = incoming.querySelectorAll<HTMLAnchorElement>(".site-header nav a");
	links.forEach((link, index) => {
		const other = fresh[index];
		if (!other) return;
		if (other.hasAttribute("aria-current")) link.setAttribute("aria-current", "page");
		else link.removeAttribute("aria-current");
	});
	const back = document.querySelector<HTMLInputElement>('form[data-enhance="theme"] input[name="back"]');
	if (back) back.value = location.pathname + location.search;
}

/* Small conveniences ------------------------------------------------------ */

function shareUrl(): string | null {
	return document.querySelector<HTMLInputElement>("#share-url")?.value || null;
}

/** Reveal the controls that only work with scripting. Nothing is created. */
function syncOptionalControls(): void {
	const complete = document.querySelector(".share")?.getAttribute("data-complete") === "true";
	for (const button of document.querySelectorAll<HTMLButtonElement>("[data-copy]")) {
		button.hidden = !navigator.clipboard || !complete;
	}
	for (const button of document.querySelectorAll<HTMLButtonElement>("[data-share]")) {
		button.hidden = !navigator.share || !complete;
	}

	tuneEmptyPicker();
}

/**
 * An unanswered number is an outline, and the sliders start on the same ink.
 * The server guesses black for "Auto" because it cannot see the system theme;
 * here we know, so put an untouched picker on the ink actually being drawn.
 */
function tuneEmptyPicker(): void {
	const field = document.querySelector<ColorField>("color-field[data-unset]");
	if (!field || typeof field.mix !== "object") return;

	const theme = document.documentElement.dataset.theme;
	const dark = theme === "dark" || (!theme && matchMedia("(prefers-color-scheme: dark)").matches);
	const lightness = dark ? 100 : 0;
	if (field.mix.l === lightness) return;
	field.mix = { ...field.mix, l: lightness };
}

async function copy(button: HTMLButtonElement): Promise<void> {
	const target = document.querySelector<HTMLInputElement>(button.dataset.copy ?? "");
	if (!target) return;
	const original = button.textContent;
	try {
		await navigator.clipboard.writeText(target.value);
		button.textContent = "Copied";
	} catch {
		button.textContent = "Press Ctrl+C";
	}
	setTimeout(() => {
		button.textContent = original;
	}, 1500);
}
